import React, { useContext, useRef, useState, useEffect, useMemo } from 'react';
import { useTable, useSortBy, useFilters, useRowSelect } from 'react-table';
import moment from 'moment';
import addDurationCapability from 'moment-duration-format';
import amplitude from 'amplitude-js';
import { showError } from '../services/toastService';
import styled from 'styled-components';
import i18n, { langCode } from '../i18n';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';
import { COL, TABLES, resolveColumns } from '../config/tableColumns';

import {
  getOrders,
} from '../services/invoiceService';
import {
  getPrebookings as fetchPrebookingsList,
} from '../services/prebookService';
import AppContext from '../contexts/AppContext';

import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';

import Icon from './Icon';
import IconButton from './IconButton';
import HeaderSortIcon from './HeaderSortIcon';

import HeaderText from './HeaderText';
import Spinner from './Spinner';

import PrebookTypeIcon from './PrebookTypeIcon';
import StatusBadge from './StatusBadge';
import OrderNumberCell from './OrderNumberCell';
import DurationActual from './DurationActual';
import DeviationResolutionCell from './DeviationResolutionCell';
import DeviationCell from './DeviationCell';
import { SimpleTooltip, CustomTooltip } from './Tooltip';
import {
  BookingsTableContainer,
  BookingTableThead,
  BookingTableTh,
  BookingTableTr,
  BookingTableTd,
  TrText,
  HeaderContainer,
  BookingsFilterSelect,
} from './basicComponents';

import { parseNoticeMessage, parseNoticeCustomerData, isPrebookingOngoing } from '../utils/prebooksHelpers';
import { formatDateWithYear } from '../utils/timeUtils';

addDurationCapability(moment);

const StyledInputCheckbox = styled.input`
  width: 17px;
  height: 17px;
`;

const StyledIconContainer = styled.div`
  text-align: center;
`;

const StyledTimeContainer = styled.div`
  text-align: center;
  font-size: ${({ fontSize }) => (fontSize)}px;
  position: relative;
`;

// Column ids whose content is short and fixed (icons, dates, numbers). Giving
// them a 1% width makes the browser shrink them to their content so the spare
// table width flows to the free-text columns instead (TUL-5517).
const SHRINK_TO_CONTENT_COLUMNS = [
  'selection',
  'type',
  'date',
  'orderNumber',
  'externalOrderId',
  'callType',
  'hotlinePhoneNumber',
  'durationEstimated',
  'durationActual',
  'editButton',
  'previewButton',
];

// orderNumber and externalOrderId are deliberately absent: they are supplied by
// the platform or the customer and can be long unbroken digit strings
// ('216485545413448451'), which on a single line overflowed into the
// neighbouring column (TUL-5698). OrderNumberCell wraps them instead, and keeps
// a character-based floor so an ordinary order number still fits on one line.
const SINGLE_LINE_COLUMNS = [
  'hotlinePhoneNumber',
  'durationEstimated',
  'durationActual',
];

const WIDE_TEXT_COLUMNS = ['deviation', 'deviationResolution'];

const WIDE_TEXT_COLUMN_MAX_WIDTH = 280;

const columnSelector = (ids) => ids.map((id) => `&.col-${id}`).join(',\n  ');

const StyledBookingTableTh = styled(BookingTableTh)`
  ${columnSelector(WIDE_TEXT_COLUMNS)} {
    max-width: ${WIDE_TEXT_COLUMN_MAX_WIDTH}px;
  }
  /* Let the filter dropdown follow the column width instead of forcing the
     column to be as wide as its longest option. */
  select {
    width: 100%;
    min-width: 56px;
  }
`;

const StyledBookingTableTd = styled(BookingTableTd)`
  &.col-date {
    padding: 0 8px 0 0;
  }
  &.col-language {
    text-transform: capitalize;
  }
  ${columnSelector(SHRINK_TO_CONTENT_COLUMNS)} {
    width: 1%;
  }
  ${columnSelector(SINGLE_LINE_COLUMNS)} {
    white-space: nowrap;
  }
  ${columnSelector(WIDE_TEXT_COLUMNS)} {
    max-width: ${WIDE_TEXT_COLUMN_MAX_WIDTH}px;
  }
`;

const StyledBadgeIcon = styled(Icon)`
  width: 26px;
  height: 26px;
  background-color: #eeeefe;
  border-radius: 10px;
`;

const StyledDateBlock = styled.span`
  word-spacing: 9999rem;
`;


const VerticalFlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PaddedSelectionColumnHeaderContainer = styled.div`
  padding-left: 6px;
`;

const CenterAlignDiv = styled.div`
  text-align: center;
`;

function SelectColumnFilter({
  column: { filterValue = '', setFilter, preFilteredRows, id }
}) {
  const { layout } = useContext(ThemeContext);

  const options = useMemo(() => {
    const optionsSet = new Set();
    preFilteredRows.forEach(row => {
      optionsSet.add(row.values[id]);
    });
    return [...optionsSet.values()].filter(value => {
      return value && value.length > 0;
    });
  }, [id, preFilteredRows]);

  const callTypeSelectionLocalized = {
    external: i18n('presence_conference'),
    onsite: i18n('presence_on_site'),
    remote: i18n('presence_remote'),
    notice: i18n('presence_notice'),
    data: i18n('call_type_video'),
    call: i18n('call_type_call'),
    prebook: i18n('prebooked'),
    ondemand: i18n('ondemand'),
  };

  return (
    <BookingsFilterSelect
      fontSize={layout.smallFontSize}
      value={filterValue}
      onChange={e => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">{i18n('all')}</option>
      {options.map((option, i) => {
        if (id === 'callType' || id === 'type') {
          return (
            <option key={i} value={option}>
              {callTypeSelectionLocalized[option] || option}
            </option>
          );
        }
        return (
          <option key={i} value={option}>
            {option}
          </option>
        );
      })}
    </BookingsFilterSelect>
  );
}

const getClassName = (columnId) => {
  if (columnId === 'type') {
    return 'type';
  }
  if (columnId === 'invoiceRef') {
    return 'invoiceRef';
  }
  if (columnId === 'invoiceRecipientRef') {
    return 'invoiceRecipientRef';
  }
  if (columnId === 'customerName') {
    return 'customerName';
  }
  if (columnId === 'invoiceUserGroup') {
    return 'invoiceUserGroup';
  }
  return '';
};

const getColumnClasses = (columnId) => [getClassName(columnId), `col-${columnId}`]
  .filter(Boolean)
  .join(' ');

const getPrebookStatus = (presence, interpreter, durationActual) => {
  if (presence === 'notice') {
    if (durationActual > 0) {
      return 'delivered';
    }
    if (durationActual === 0) {
      return 'not_able_to_reach';
    }
  }
  return (interpreter) ? 'confirmed' : 'pending';
};

const isRequiredFieldsFilled = (chatroomOrPrebook) => {
  if (!chatroomOrPrebook.invoiceUserGroup?.id) {
    return false;
  }
  if (chatroomOrPrebook.customFields && chatroomOrPrebook.customFields.length > 0) {
    const notFilled = chatroomOrPrebook.customFields.reduce((memo, customField) => {
      if (customField.required && !customField.value) {
        memo.push(customField);
      }
      return memo;
    }, []);
    if (notFilled.length > 0) {
      return false;
    }
  }
  return true;
};

// A reported deviation (customer feedback with improvements or free text) keeps
// the past interpretation in the invoicing list until it is resolved, so its row
// must not be batch-confirmed away — confirming would clear it (TUL-4891 / TUL-4863).
// Deviations are a Hero-only concept.
const isDeviationReported = (deviation) => {
  if (!deviation) {
    return false;
  }
  const hasImprovements = Array.isArray(deviation.improvements) && deviation.improvements.length > 0;
  const hasFreeText = typeof deviation.freeText === 'string' && deviation.freeText.trim().length > 0;
  return hasImprovements || hasFreeText;
};

const hasUnresolvedDeviation = (deviation, deviationResolution) =>
  isHeroBrandEnabled() && isDeviationReported(deviation) && !deviationResolution;

const prepareOngoingPrebookings = (ongoingPrebookings) => {
  return ongoingPrebookings.map((booking) => ({
    id: booking.id,
    date: new Date(booking.interpretationDate),
    callType: (booking.presence === 'onsite' || booking.presence === 'notice' || (booking.presence === 'external' && booking.callType === 'data')) ? booking.presence : booking.callType,
    language: booking.interpretedLanguage?.name || '',
    status: 'ongoing',
    durationEstimated: booking.durationEstimated,
    durationActual: null,
    durationActualStartedAt: booking.durationActualStartedAt || null,
    durationActualEndedAt: booking.durationActualEndedAt || null,
    interpretationDate: booking.interpretationDate || null,
    leewayBefore: booking.leewayBefore,
    interpreter: booking.interpreter,
    interpreterName: booking.interpreter?.name || '',
    orderNumber: booking.orderNumber,
    externalOrderId: booking.externalOrderId,
    customerId: booking.customer?.id,
    customerName: (booking.customer && booking.customer.name) ? booking.customer.name : '',
    invoiceRef: booking.invoiceRef || '',
    invoiceUserGroup: (booking.invoiceUserGroup && booking.invoiceUserGroup.name) ? booking.invoiceUserGroup.name : '',
    invoiceRecipientRef: booking.invoiceRecipientRef ? booking.invoiceRecipientRef : '',
    hotlinePhoneNumber: booking.hotlinePhoneNumber,
    deviation: Array.isArray(booking.feedbacks) && booking.feedbacks.length > 0
      ? booking.feedbacks[0]
      : null,
    deviationResolution: booking.deviationResolution || null,
    type: 'prebook',
    notAvailableForBatchConfirmation: true,
    isOngoing: true,
  }));
};

const preparePrebookings = (prebookings, chatrooms, ongoingPrebookings = []) => {
  const preparedChatrooms = chatrooms.map((chatroom) => ({
    id: chatroom.id,
    date: new Date(chatroom.createdAt),
    callType: (chatroom.callType === 'data') ? 'external' : chatroom.callType,
    language: chatroom.interpretedLanguage.name,
    status: (chatroom && chatroom.interpreter) ? 'confirmed' : 'pending',
    durationEstimated: chatroom.billableDuration,
    durationActual: null,
    durationActualStartedAt: chatroom.durationActualStartedAt || null,
    durationActualEndedAt: chatroom.durationActualEndedAt || null,
    interpretationDate: chatroom.createdAt || null,
    interpreter: chatroom.interpreter,
    interpreterName: chatroom.interpreter?.name || '',
    orderNumber: chatroom.orderNumber,
    externalOrderId: chatroom.externalOrderId,
    customerId: chatroom?.customer?.id,
    customerName: chatroom?.customer?.name ? chatroom.customer.name : '',
    invoiceRef: chatroom.invoiceRef || '',
    invoiceUserGroup: (chatroom.invoiceUserGroup && chatroom.invoiceUserGroup.name) ? chatroom.invoiceUserGroup.name : '',
    invoiceRecipientRef: chatroom.invoiceRecipientRef ? chatroom.invoiceRecipientRef : '',
    hotlinePhoneNumber: null,
    deviation: null,
    deviationResolution: null,
    type: 'ondemand',
    notAvailableForBatchConfirmation: !isRequiredFieldsFilled(chatroom),
  }));
  const preparedPrebookings = prebookings.map((booking) => {
    const deviation = Array.isArray(booking.feedbacks) && booking.feedbacks.length > 0
      ? booking.feedbacks[0]
      : null;
    const deviationResolution = booking.deviationResolution || null;
    return {
      id: booking.id,
      date: new Date(booking.interpretationDate),
      callType: (booking.presence === 'onsite' || booking.presence === 'notice' || (booking.presence === 'external' && booking.callType === 'data')) ? booking.presence : booking.callType,
      language: booking.interpretedLanguage?.name || '',
      status: getPrebookStatus(booking.presence, booking?.interpreter, booking.durationActual),
      durationEstimated: booking.durationEstimated,
      durationActual: booking.durationActual,
      durationActualStartedAt: booking.durationActualStartedAt || null,
      durationActualEndedAt: booking.durationActualEndedAt || null,
      interpretationDate: booking.interpretationDate || null,
      leewayBefore: booking.leewayBefore,
      interpreter: booking.interpreter,
      interpreterName: booking.interpreter?.name || '',
      orderNumber: booking.orderNumber,
      externalOrderId: booking.externalOrderId,
      customerId: booking.customer?.id,
      customerName: (booking.customer && booking.customer.name) ? booking.customer.name : '',
      invoiceRef: booking.invoiceRef || '',
      invoiceUserGroup: (booking.invoiceUserGroup && booking.invoiceUserGroup.name) ? booking.invoiceUserGroup.name : '',
      invoiceRecipientRef: booking.invoiceRecipientRef ? booking.invoiceRecipientRef : '',
      hotlinePhoneNumber: booking.hotlinePhoneNumber,
      deviation,
      deviationResolution,
      type: 'prebook',
      notAvailableForBatchConfirmation:
        !isRequiredFieldsFilled(booking) || hasUnresolvedDeviation(deviation, deviationResolution),
    };
  });
  const preparedOngoing = prepareOngoingPrebookings(ongoingPrebookings);
  const ongoingIds = new Set(preparedOngoing.map((o) => o.id));
  const dedupedPrebookings = preparedPrebookings.filter((p) => !ongoingIds.has(p.id));
  return [
    ...preparedOngoing,
    ...dedupedPrebookings,
    ...preparedChatrooms
  ].sort((a, b) => new Date(b.date) - new Date(a.date));
};

const IndeterminateCheckbox = React.forwardRef(
  ({
    indeterminate,
    ...rest
  }, ref) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <StyledInputCheckbox type="checkbox" ref={resolvedRef} {...rest} />
      </>
    );
  }
);

const parseMessagesIfNeeded = (prebookings) => {
  return prebookings.map((booking) => {
    if (booking.presence === 'notice') {
      const parsedNoticeMessage = parseNoticeMessage(booking.messageToInterpreter);
      const parsedNoticeCustomerData = parseNoticeCustomerData(booking.invoiceRef);

      return {
        ...booking,
        customerData: parsedNoticeCustomerData.customerData,
        customerDataBirthday: parsedNoticeCustomerData.customerBirthday,
        alternativeContactPerson: parsedNoticeMessage.alternativeContactPerson,
        mainPhoneNumber: parsedNoticeMessage.phoneNumbers[0],
        phoneNumbers: parsedNoticeMessage.phoneNumbers?.slice(1)?.join(' '),
        messageToCustomerService: booking.comments,
        contentData: parsedNoticeMessage.contentData,
      };
    }
    return booking;
  });
};

const getEditToolTipText = () => {
  if (isHeroBrandEnabled()) {
    return i18n('edit_or_create_deviation', i18n('edit'));
  }
  return i18n('edit');
};


const alarmColumn = (iconColor) => {
  return {
    Header: () => (
      <StyledBadgeIcon
        icon={'alarm'}
        iconLib="material"
        iconSize="18"
        iconColor={iconColor}
      />
    ),
    accessor: 'type',
    Cell: ({ cell: { row }}) => {
      return row?.original?.type ? (
        <CustomTooltip
          direction="right"
          text={row.original.type === 'prebook' ? i18n('prebooked') : i18n('ondemand')}
        >
          <StyledBadgeIcon
            icon={row.original.type === 'prebook' ? 'alarm' : 'alarm_off'}
            iconLib="material"
            iconSize="18"
            iconColor={iconColor}
            data-tip={i18n('call_type_video')}
            data-event="hover"
          />
        </CustomTooltip>
      ) : null;
      },
    Filter: SelectColumnFilter,
    filter: 'includes'
  };
};



const selectionColumn = (fontSize, color) => {
  return {
    id: 'selection',
    Header: ({ getToggleAllRowsSelectedProps }) => (
      <VerticalFlexContainer>
        <TrText hero fontSize={fontSize} color={color}>{i18n('choose_button')}</TrText>
        <PaddedSelectionColumnHeaderContainer>
          <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
        </PaddedSelectionColumnHeaderContainer>
      </VerticalFlexContainer>
    ),
    Cell: ({ row }) => {
      if (row?.original?.notAvailableForBatchConfirmation) {
        const { checked, ...rest } = row.getToggleRowSelectedProps();
        const blockedByDeviation = hasUnresolvedDeviation(row?.original?.deviation, row?.original?.deviationResolution);
        return (
          <CenterAlignDiv>
            <CustomTooltip
              direction="top"
              text={blockedByDeviation
                ? i18n('deviation_must_be_resolved_before_confirming')
                : i18n('this_invoice_needs_additional_information')}
            >
              <IndeterminateCheckbox
                {...rest}
                {...(!row?.original?.notAvailableForBatchConfirmation ? {
                  checked
                } : {})}
                disabled={row?.original?.notAvailableForBatchConfirmation}
              />
            </CustomTooltip>
          </CenterAlignDiv>
        );
      }
      return (
        <CenterAlignDiv>
          <IndeterminateCheckbox
            {...row.getToggleRowSelectedProps()}
          />
        </CenterAlignDiv>
      );
    },
  };
};

const firstColumn = (layout, colors, approveForInvoicingIsOnForHerotolk) => {
  return isHeroBrandEnabled() && approveForInvoicingIsOnForHerotolk ? selectionColumn(layout.smallFontSize, colors.webDarkBlue) : alarmColumn(colors.webDarkBlue);
};


const BookingsTable = ({ openPreview, active, setItemsForVerify, approveForInvoicingIsOnForHerotolk }) => {
  moment.locale(langCode);

  const { colors, layout } = useContext(ThemeContext);
  const { user } = useContext(AppContext);
  const { sessionExpired } = useContext(FunctionsContext);

  const _isMounted = useRef(true); // Initial value _isMounted = true

  useEffect(() => {
    return () => { // ComponentWillUnmount in Class Component
      _isMounted.current = false;
    };
  }, []);

  const [prebookings, setPrebookings] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [ongoingPrebookings, setOngoingPrebookings] = useState([]);
  const [indicator, setIndicator] = useState(false);
  const [updateIndicator, setUpdateIndicator] = useState(false);
  const [ongoingTick, setOngoingTick] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setOngoingTick((tick) => tick + 1);
    }, 30000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const extendWithType = (prebooks, type) => {
    return prebooks.map((prebook) => ({
      ...prebook,
      type: type,
    }));
  };

  const fetchOngoingPrebookings = async () => {
    try {
      const result = await fetchPrebookingsList(false, user?.type);
      const list = (result?.prebookings || []).filter(isPrebookingOngoing);
      return parseMessagesIfNeeded(list);
    } catch (e) {
      if (e.message === 'Session expired') {
        sessionExpired();
      } else {
        console.error('Failed to fetch ongoing prebookings', e);
      }
      return [];
    }
  };

  const getPrebookings = async () => {
    setIndicator(true);
    try {
      const [result, ongoing] = await Promise.all([
        getOrders(),
        fetchOngoingPrebookings(),
      ]);
      if (_isMounted.current) {
        setPrebookings(extendWithType(parseMessagesIfNeeded(result.prebookings), 'prebook'));
        setChatRooms(extendWithType(result.chatRooms, 'ondemand'));
        setOngoingPrebookings(extendWithType(ongoing, 'prebook'));
        setIndicator(false);
        setUpdateIndicator(false);
        document.dispatchEvent(new CustomEvent('updateInvoiceCount', {
          detail: {
            count: (result.prebookings.length || 0) + (result.chatRooms.length || 0)
          }
        }));
      }
    } catch (e) {
      console.log('Failed to get orders.', e);

      if (e.response !== undefined && e.response.status === 400) {
        amplitude.getInstance().logEvent('Accept request failed', { 'HTTP status code': e.response ? String(e.response.status) : '' });
        if (_isMounted.current) {
          showError(i18n('error500'));
        }
      } else if (e.message === 'Session expired') {
        sessionExpired();
      } else {
        amplitude.getInstance().logEvent('Accept request failed', { 'HTTP status code': e.response ? String(e.response.status) : '' });
        if (_isMounted.current) {
          showError(i18n('error500'));
        }
      }
      if (_isMounted.current) {
        setIndicator(false);
      }
    }
  };

  useEffect(() => {
    const func = async () => {
      setUpdateIndicator(true);
      await getPrebookings();
    };
    document.addEventListener('reloadBillingPrebooksTable', func);
    return () => {
      document.removeEventListener('reloadBillingPrebooksTable', func);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (active !== false) {
      getPrebookings();
    }
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPreviewModal = (prebook, editable) => {
    const prebookForPreview = prebookings.find((aPrebook) => aPrebook.id === prebook.id)
      || ongoingPrebookings.find((aPrebook) => aPrebook.id === prebook.id)
      || chatRooms.find((chatRoom) => chatRoom.id === prebook.id);
    if (!prebookForPreview) {
      return;
    }
    if (prebookForPreview.type === 'ondemand') {
      openPreview({
        ...prebookForPreview,
        presence: 'external',
        type: 'ondemand',
      }, editable);
    } else {
      openPreview({
        ...prebookForPreview,
        type: 'prebook',
      }, editable);
    }
  };

  const handleRowClick = (prebook) => {
    openPreviewModal(prebook, true);
  };

  const isExistReservationNumber = (prebookingsArray, chatRoomsArray) => (
    prebookingsArray.some((elem) => elem.orderNumber) || chatRoomsArray.some((elem) => elem.orderNumber)
  );

  const isExistExternalOrderId = (prebookingsArray, chatRoomsArray) => (
    prebookingsArray.some((elem) => elem.externalOrderId) || chatRoomsArray.some((elem) => elem.externalOrderId)
  );

  const columns = useMemo(() => {
    const definitions = {
      [COL.FIRST]: firstColumn(layout, colors, approveForInvoicingIsOnForHerotolk),
      [COL.TIME]: {
        Header: i18n('time'),
        accessor: 'date',
        Cell: ({ cell: { value }}) => {
          const date = moment(value);
          const formattedDatePart = formatDateWithYear(date);
          const formattedDateTime = `${formattedDatePart} ${date.format('HH:mm')}`;
          return (
            <StyledTimeContainer
              fontSize={layout.smallFontSize}
            >
              <StyledDateBlock>
                {formattedDateTime}
              </StyledDateBlock>
            </StyledTimeContainer>
          );
        },
        sortType: 'datetime'
      },
      [COL.LANGUAGE]: {
        Header: i18n('language_title'),
        accessor: 'language',
        sortType: 'basic'
      },
      [COL.STATUS]: {
        Header: i18n('status'),
        accessor: 'status',
        sortType: 'basic',
        Cell: ({ cell: { value } }) => {
          if (isHeroBrandEnabled() && value !== 'ongoing') {
            return null;
          }
          return <StatusBadge status={value} />;
        }
      },
      [COL.DURATION]: {
        Header: i18n('prebooking_duration'),
        accessor: 'durationEstimated',
        sortType: 'basic',
        Cell: ({ cell: { value }}) => {
          return moment.duration(value, 'seconds').format('hh:mm', {
            trim: false
          });
        },
      },
      [COL.REPORTED_DURATION]: {
        Header: i18n('interpreter_reported_duration'),
        accessor: 'durationActual',
        sortType: 'basic',
        Cell: ({ cell: { value, row }}) => (
          <DurationActual
            value={value}
            startedAt={row?.original?.durationActualStartedAt}
            endedAt={row?.original?.durationActualEndedAt}
            interpretationDate={row?.original?.interpretationDate}
            color={colors.heroPurple}
          />
        ),
      },
      [COL.INTERPRETER_NAME]: {
        Header: i18n('interpreter_name'),
        accessor: 'interpreterName',
        Filter: SelectColumnFilter,
        filter: 'includes',
        sortType: 'basic',
      },
      [COL.CALL_TYPE]: {
        Header: i18n('call_type_title'),
        accessor: 'callType',
        Filter: SelectColumnFilter,
        filter: 'includes',
        Cell: ({ cell: { value }}) => (
          <PrebookTypeIcon
            type={value}
          />
        ),
      },
      [COL.INTERPRETER_PHONE]: {
        Header: i18n('interpreter_phone_number'),
        accessor: 'hotlinePhoneNumber',
        sortType: 'basic',
      },
      [COL.CUSTOMER]: {
        Header: i18n('customer'),
        accessor: 'customerName',
        Filter: SelectColumnFilter,
        filter: 'includes',
        sortType: 'basic',
      },
      [COL.USER_GROUP]: {
        Header: i18n('invoice_user_group'),
        accessor: 'invoiceUserGroup',
        Filter: SelectColumnFilter,
        filter: 'includes',
        sortType: 'basic',
      },
      [COL.COST_CENTER]: {
        Header: i18n('customerInvoiceNotes'),
        accessor: 'invoiceRef',
        Filter: SelectColumnFilter,
        filter: 'includes',
        sortType: 'basic',
        Cell: ({ cell: { value, row }}) => {
          if (row.original.callType === 'notice') {
            return '';
          }
          return value;
        },
      },
      [COL.DEVIATION]: {
        Header: i18n('deviation'),
        id: 'deviation',
        accessor: row => (row.deviation?.improvements || [])
          .map(item => (typeof item === 'string' ? item : (item?.value || item?.key)) || '')
          .filter(Boolean)
          .join(', '),
        sortType: 'basic',
        disableFilters: true,
        Cell: ({ row: { original } }) => (
          <DeviationCell value={original.deviation} />
        ),
      },
      [COL.DEVIATION_RESOLUTION]: {
        Header: i18n('deviation_resolution'),
        id: 'deviationResolution',
        accessor: row => row.deviationResolution?.resolutionNotes || '',
        sortType: 'basic',
        disableFilters: true,
        Cell: ({ row: { original } }) => (
          <DeviationResolutionCell value={original.deviationResolution} />
        ),
      },
      [COL.ALT_COST_CENTER]: {
        Header: i18n('alternative_cost_center'),
        accessor: 'invoiceRecipientRef',
        Filter: SelectColumnFilter,
        filter: 'includes',
        sortType: 'basic',
      },
    };

    if (isExistReservationNumber(prebookings, chatRooms)) {
      definitions[COL.ORDER_NUMBER] = {
        Header: i18n('reservation_number'),
        accessor: 'orderNumber',
        sortType: 'basic',
        Cell: ({ cell: { value } }) => <OrderNumberCell value={value} />
      };
    }

    if (isExistExternalOrderId(prebookings, chatRooms)) {
      definitions[COL.EXTERNAL_ORDER_ID] = {
        Header: user?.userGroup?.externalOrderIdTitle || i18n('external_order_id'),
        accessor: 'externalOrderId',
        sortType: 'basic',
        Cell: ({ cell: { value } }) => <OrderNumberCell value={value} />
      };
    }

    definitions[COL.EDIT] = {
      Header: i18n('edit'),
      accessor: 'editButton',
      Cell: (props) => {
        if (props?.row?.original?.isOngoing) {
          return <StyledIconContainer />;
        }
        return (
          <StyledIconContainer>
            <CustomTooltip
              direction="top"
              text={getEditToolTipText()}
            >
              <IconButton
                icon="edit"
                iconLib="material"
                iconColor={colors.webDarkBlue}
                hoverColor={colors.tulkaMainColor}
                onClick={() => {
                  if (props && props.row && props.row.original && props.row.original.id) {
                    openPreviewModal(props.row.original, true);
                  }
                }}
              />
            </CustomTooltip>
          </StyledIconContainer>
        );
      },
    };

    definitions[COL.PREVIEW] = {
      Header: i18n('preview'),
      accessor: 'previewButton',
      Cell: (props) => {
        return (
          <StyledIconContainer>
            <CustomTooltip
              direction="top"
              text={i18n('preview')}
            >
              <IconButton
                icon="eye"
                iconLib="fontAwesome"
                aria-label={i18n('preview')}
                iconColor={colors.webDarkBlue}
                hoverColor={colors.tulkaMainColor}
                onClick={() => {
                  if (props && props.row && props.row.original && props.row.original.id) {
                    openPreviewModal(props.row.original, false);
                  }
                }}
              />
            </CustomTooltip>
          </StyledIconContainer>
        );
      }
    };

    definitions[COL.SELECTION] = selectionColumn(layout.smallFontSize);

    return resolveColumns(TABLES.BILLING, definitions);
  }, [prebookings, chatRooms, ongoingPrebookings, colors, layout, approveForInvoicingIsOnForHerotolk, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => {
    const stillOngoing = ongoingPrebookings.filter(isPrebookingOngoing);
    return preparePrebookings(prebookings, chatRooms, stillOngoing) || [];
  }, [ prebookings, chatRooms, ongoingPrebookings, ongoingTick ]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableInstance = useTable({ columns, data, autoResetFilters: false }, useFilters, useSortBy, useRowSelect);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setAllFilters,
  } = tableInstance;

  useEffect(() => {
    if ((rows && rows.length === 0) && (data && data.length > 0) && state.filters.length > 0) {
      setAllFilters([]);
    }
  }, [data, rows, state.filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const itemsObj = rows.reduce((memo, row) => {
      if (state.selectedRowIds[row.id] && !row?.original?.notAvailableForBatchConfirmation) {
        return memo.concat({
          id: row.original.id,
          type: row.original.type
        });
      }
      return memo;
    }, []);
    setItemsForVerify(itemsObj);
  }, [rows, state.selectedRowIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const isHero = isHeroBrandEnabled();

  return (
    <div>
      <BookingsTableContainer
        fontSize={layout.smallFontSize}
        {...getTableProps()}
      >
        <BookingTableThead>
          {headerGroups.map(headerGroup => {
            return (
              <tr {...headerGroup.getHeaderGroupProps()} valign="top">
                {headerGroup.headers.map((column, index) => {
                  return (
                    <StyledBookingTableTh
                      lang={langCode}
                      className={getColumnClasses(column.id)}
                      key={index}
                    >
                      <TrText
                        hero={isHero}
                        color={colors.webDarkBlue}
                        fontSize={layout.smallFontSize}
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                      >
                        {column.render('Header')}
                        {column.id !== 'selection' && column.id !== 'previewButton' && column.id !== 'editButton' && column.id !== 'type' ? (
                          <HeaderSortIcon iconColor={colors.webDarkBlue} />
                        ): null}
                      </TrText>
                      <div>
                        {column.Filter ? column.render('Filter') : null}
                      </div>
                    </StyledBookingTableTh>
                  );
                })}
              </tr>
            );
          })}
        </BookingTableThead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <BookingTableTr
                {...row.getRowProps()}
                onClick={(event) => {
                  if (event.target.nodeName.toLowerCase() !== 'input' && event.target.nodeName.toLowerCase() !== 'i' && event.target.nodeName.toLowerCase() !== 'button' && row && row.original && row.original.id) {
                    handleRowClick(row.original);
                  }
                }}
              >
                {row.cells.map(cell => {
                  return (
                    <StyledBookingTableTd
                      className={getColumnClasses(cell.column.id)}
                      {...cell.getCellProps()}
                    >
                      {
                        cell.column.id !== 'type' && getClassName(cell.column.id) ? (
                          <SimpleTooltip
                            direction="top"
                            text={cell.render('Cell')}
                          >
                            {cell.render('Cell')}
                          </SimpleTooltip>
                        ) : cell.render('Cell')
                      }
                    </StyledBookingTableTd>
                  );
                })}
              </BookingTableTr>
            );
          })}
        </tbody>
      </BookingsTableContainer>
      {updateIndicator ? (
        <Spinner
          overlay
        />
      ): null}
      {(rows.length === 0) && (
        <HeaderContainer>
          {indicator ? (
            <Spinner/>
          ): !isHeroBrandEnabled() && (
            <HeaderText
              text={i18n('no_invoices')}
              fontSize={layout.headerFontSize}
            />
          )}
        </HeaderContainer>
      )}
    </div>
  );
};

export default BookingsTable;
